import { useEffect, useRef, RefObject } from "react";

interface UseDialogOptions {
  /** Whether the dialog is currently open. No listeners attach, and no focus is moved, while false. */
  isOpen: boolean;
  /** Called on Escape press. Put any extra close-guard logic in here. */
  onClose: () => void;
  /** Skip the Escape listener (default: false — Escape closes by default). */
  disableEscape?: boolean;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared accessible-dialog behavior for real modal dialogs (backdrop +
 * centered panel), consolidating the hand-rolled outside-click-only
 * versions across the app that had no focus trap, no return-focus-on-close,
 * and inconsistent Escape handling (see ADMIN_PANEL_AUDIT.md §7.1).
 *
 * Attach the returned ref to the dialog panel (the element that should also
 * carry role="dialog" aria-modal="true" tabIndex={-1} in the JSX — this hook
 * only manages behavior, not those static attributes). Distinct from
 * usePopover, which targets outside-click-dismissible dropdowns/popovers,
 * not modal dialogs that need a focus trap and return-focus.
 */
export function useDialog<T extends HTMLElement = HTMLDivElement>({
  isOpen,
  onClose,
  disableEscape = false,
}: UseDialogOptions): RefObject<T> {
  const ref = useRef<T>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const container = ref.current;

    const getFocusable = () =>
      container
        ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        : [];

    // Move focus into the dialog so screen readers announce it and Tab
    // starts from inside the trap, not wherever the trigger button was.
    const focusables = getFocusable();
    (focusables[0] ?? container)?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (!disableEscape) onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const items = getFocusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [isOpen, disableEscape]);

  return ref;
}
